import { useMemo } from 'react';
import { X, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useAppStore } from '@/stores/appStore';

const statusStyles: Record<string, string> = {
  queued: 'bg-amber-500/10 text-amber-200 border-amber-500/20',
  running: 'bg-blue-500/10 text-blue-200 border-blue-500/20',
  succeeded: 'bg-emerald-500/10 text-emerald-200 border-emerald-500/20',
  failed: 'bg-rose-500/10 text-rose-200 border-rose-500/20',
};

const statusIcon = (status: string) => {
  if (status === 'running') return <Loader2 className="w-3 h-3 animate-spin" />;
  if (status === 'succeeded') return <CheckCircle2 className="w-3 h-3" />;
  if (status === 'failed') return <AlertTriangle className="w-3 h-3" />;
  return <div className="w-3 h-3 rounded-full bg-amber-400" />;
};

export function ScrapeStatusBar() {
  const { scrapeJobs, removeScrapeJob, clearCompletedJobs, isAuthenticated } = useAppStore();

  const { running, queued, completed } = useMemo(() => {
    const running = scrapeJobs.filter((j) => j.status === 'running');
    const queued = scrapeJobs.filter((j) => j.status === 'queued');
    const completed = scrapeJobs.filter((j) => j.status === 'succeeded' || j.status === 'failed');
    return { running, queued, completed };
  }, [scrapeJobs]);

  if (!isAuthenticated) return null;
  if (scrapeJobs.length === 0) return null;

  return (
    <div className="fixed bottom-4 left-1/2 z-[60] w-[95%] max-w-4xl -translate-x-1/2 rounded-2xl border border-gray-800/70 bg-[#0c0e14]/95 backdrop-blur shadow-xl">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800/70">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-gray-400">Scrape Status</p>
          <p className="text-sm text-gray-200">
            {running.length} running · {queued.length} queued · {completed.length} done
          </p>
        </div>
        <div className="flex items-center gap-2">
          {completed.length > 0 && (
            <button
              onClick={clearCompletedJobs}
              className="text-xs px-2 py-1 rounded bg-gray-800/70 text-gray-200 hover:bg-gray-700/70"
            >
              Clear Done
            </button>
          )}
        </div>
      </div>
      <div className="max-h-40 overflow-auto px-3 py-3 grid grid-cols-1 md:grid-cols-2 gap-2">
        {scrapeJobs.map((job) => (
          <div
            key={job.id}
            className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${statusStyles[job.status] ?? 'bg-gray-800/40 text-gray-200 border-gray-700/60'}`}
          >
            <div className="flex items-center gap-2">
              {statusIcon(job.status)}
              <span className="text-xs font-semibold uppercase">{job.status}</span>
            </div>
            <div className="flex-1">
              <div className="text-xs text-gray-300">{job.label}</div>
              <div className="text-[11px] text-gray-400">{job.message}</div>
              <div className="mt-1 h-1 w-full rounded-full bg-black/30">
                <div
                  className="h-1 rounded-full bg-gradient-to-r from-indigo-400 to-blue-500"
                  style={{ width: `${Math.max(3, job.progress)}%` }}
                />
              </div>
            </div>
            <button
              onClick={() => removeScrapeJob(job.id)}
              className="text-gray-400 hover:text-gray-200"
              title="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
